import React, { useEffect } from "react";
import FormLayout, { FormItem, FormSection } from "Components/form";
import { Col, Select, Row, InputNumber, Input, message, Button } from "antd";
import FetchSelect from "Components/fetch-select";
import { clean } from "Utils/";
import { CURRENCY } from "Constants/";
import { yupResolver } from "@hookform/resolvers";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import DatePicker from "Components/date-picker-localized";
import DocumentUploader from "Components/form/uploader";
import {
  DOCUMENT_STATUS,
  DOCUMENT_LANGUAGE,
  DOCUMENT_REF_TYPE,
} from "Constants/document";
import API from "API";
import moment from "moment";
import { PaymentStoreModel, ACTION_TYPES } from "store/models/payments";
import { createComponentStore } from "easy-peasy";
import { paymentValidationSchema } from "ValidationModels/payments";
import { fixDates } from "Utils/";
import Documents from "Components/form/documents-list";

const usePaymentStore = createComponentStore(PaymentStoreModel);

export default function PaymentForm({
  contract,
  onSubmit,
  payment = {},
  ...props
}) {
  const [store, actions] = usePaymentStore();

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(paymentValidationSchema),
  });

  const {
    control,
    handleSubmit,
    errors,
    formState,
    reset,
    watch,
    setValue,
    getValues,
  } = form;

  const filesFieldArray = useFieldArray({
    control: control,
    name: "files",
    keyName: "key",
  });

  const { fields: files } = filesFieldArray;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      message.error("Tahsilat için girdiğiniz değerleri kontrol ediniz.");
    }
  }, [errors]);

  useEffect(() => {
    if (payment) reset(fixDates({ ...payment, files: [] }));
  }, [payment]);

  useEffect(() => {
    switch (store.process) {
      case ACTION_TYPES.CREATED_DATA:
        message.success("Tahsilat eklendi.");
        break;
      case ACTION_TYPES.UPDATED_DATA:
        message.success("Tahsilat güncellendi.");
        break;
      default:
        break;
    }
  }, [store.process]);

  const uploadDoc = async (ref) => {
    const formData = new FormData();
    let fileInfos = [];

    const values = getValues();

    if (ref && files.length > 0) {
      files.forEach((item, index) => {
        formData.append("files", item.file);

        let fileInfo = {
          referenceType: {
            id: DOCUMENT_REF_TYPE.payment,
          },
          referenceId: ref.id,
          documentName: values.files[index].documentName,
          releaseDate: moment(),
          status: { id: DOCUMENT_STATUS["Yayında"] },
          language: {
            id: DOCUMENT_LANGUAGE["Türkçe"],
          },
          revision: false,
        };
        fileInfos.push(fileInfo);
      });

      formData.append("json", JSON.stringify(fileInfos));
      const response = await API.post("/documents/uploadDocs", formData);
      return response;
    }
  };

  const formOnSubmit = async (form) => {
    delete form.files;
    clean(form);

    try {
      if (payment) {
        const response = await actions.update({
          ...payment,
          ...form,
          id: payment.id,
          contract: contract,
        });
        await uploadDoc(response);
      } else {
        const response = await actions.create({
          ...payment,
          ...form,
          contract: contract,
        });
        await uploadDoc(response);
      }

      if (onSubmit) onSubmit();
    } catch (error) {
      if (error.errors?.length > 0) message.error(error.errors[0]);
      else message.error(error.message || error);
    }
  };

  return (
    <form onSubmit={handleSubmit(formOnSubmit)}>
      <FormLayout>
        <Col xs={24} md={12}>
          <FormItem
            label="Tahsilat Türü"
            required={true}
            error={errors.conditionType?.id}
          >
            <FetchSelect
              api="/masterdata/conditiontype"
              form={form}
              field="conditionType"
              placeholder="Tahsilat Türü seçiniz"
            />
          </FormItem>
        </Col>
        <Col xs={24} sm={12}>
          <FormItem
            label="Tahsilat Tarihi"
            required={true}
            error={errors.paymentDate}
          >
            <Controller
              control={control}
              name="paymentDate"
              as={
                <DatePicker
                  format="DD.MM.YYYY"
                  disabledDate={(current) => current > moment().endOf("day")}
                  style={{ width: "100%" }}
                />
              }
            />
          </FormItem>
        </Col>
        <Col xs={24} md={12}>
          <Row>
            <Col xs={24} sm={12} lg={16}>
              <FormItem label="Tutar" required={true} error={errors.amount}>
                <Controller
                  control={control}
                  name="amount"
                  as={
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={50}
                      decimalSeparator=","
                    />
                  }
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <FormItem
                label="Para Birimi"
                required={true}
                error={errors.currency}
              >
                <Controller
                  control={control}
                  name="currency"
                  as={
                    <Select>
                      {Object.entries(CURRENCY).map((c) => (
                        <Select.Option value={c[0]} key={c[0]}>
                          {c[1]}
                        </Select.Option>
                      ))}
                    </Select>
                  }
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
        <Col xs={24}>
          <FormItem label="Açıklama" error={errors.description}>
            <Controller
              control={control}
              name="description"
              as={<Input.TextArea rows={3} placeholder="Açıklama yazınız" />}
            />
          </FormItem>
        </Col>
        <FormSection title="Ekler">
          <DocumentUploader
            form={form}
            fieldArray={filesFieldArray}
            multiple={false}
          />
          <Documents
            documents={payment?.documents}
            mode="edit"
            onRemove={() => {
              if (onSubmit) onSubmit();
            }}
          />
        </FormSection>
        <Col xs={24} style={{ marginTop: 24 }}>
          <Button
            loading={
              store.process === ACTION_TYPES.CREATING_DATA ||
              store.process === ACTION_TYPES.UPDATING_DATA
            }
            style={{ width: 120 }}
            type="primary"
            htmlType="submit"
          >
            Kaydet
          </Button>
        </Col>
      </FormLayout>
    </form>
  );
}
